'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { signIn } from '@/lib/auth';
import { setSession } from '@/lib/sessionStore';
import { T, ORANGE, MONO, BODY, cardStyle, btnPrimary, labelStyle } from '@/lib/cleanTokens';
import { CLEAN_CSS } from '@/components/CleanPrimitives';

const LockIcon = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <rect x="2" y="4.5" width="6" height="4.5" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
    <path d="M3.5 4.5V3.5a1.5 1.5 0 013 0v1" stroke="currentColor" strokeWidth="1" fill="none" />
  </svg>
);

export default function LoginContent() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Field state — kept only in component memory, never serialised to URL
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Ref to track if we already submitted (prevents double-submit)
  const submittingRef = useRef(false);

  // On mount: wipe any query string from the URL so previously-leaked
  // credentials are removed from the address bar and history entry.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const validate = (): boolean => {
    let valid = true;
    if (!username || username.length < 2) {
      setUsernameError('Username is required');
      valid = false;
    } else {
      setUsernameError(null);
    }
    if (!password || password.length < 6) {
      setPasswordError('Password is required');
      valid = false;
    } else {
      setPasswordError(null);
    }
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // CRITICAL: intercept native form submission — credentials must never reach the URL
    e.preventDefault();

    // Acceptance proof line — urlHasCreds checks that neither field value
    // appears in the current query string (should always be false after mount wipe)
    const qs = typeof window !== 'undefined' ? window.location.search : '';
    const urlHasCreds =
      (username.length > 0 && qs.includes(encodeURIComponent(username))) ||
      (password.length > 0 && qs.includes(encodeURIComponent(password)));
    console.log(`AUTH SUBMIT preventedDefault=true urlHasCreds=${urlHasCreds}`);
    console.log(`AUTH persist=${rememberMe}`);

    if (!validate()) return;
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsLoading(true);
    setInlineError(null);

    try {
      // Credentials passed only in the Supabase call body — never in URL, never logged
      const { data: authData, error } = await signIn(username, password);

      if (error || !authData?.user) {
        const msg = error?.message ?? 'Sign in failed';
        const friendly =
          msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')
            ? 'Invalid username or password. Check with your admin.'
            : msg;
        setInlineError(friendly);
        setIsLoading(false);
        submittingRef.current = false;
        return;
      }

      const { getOwnProfile } = await import('@/lib/auth');
      const profile = await getOwnProfile(authData.user.id);

      if (!profile) {
        setInlineError('Profile not found. Contact your admin.');
        setIsLoading(false);
        submittingRef.current = false;
        return;
      }

      setSession({
        username: profile.username,
        galaxy_name: profile.galaxy_name,
        role: profile.role,
        model: profile.model,
        show_money: profile.show_money,
        disabled: profile.disabled,
        must_change_password: profile.must_change_password,
      });

      // If Remember Me is unchecked, move Supabase session to sessionStorage
      // so it doesn't survive a browser restart (tab/window close clears it).
      if (!rememberMe && typeof window !== 'undefined') {
        const SUPA_KEY = `sb-${process.env.NEXT_PUBLIC_SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`;
        const stored = window.localStorage.getItem(SUPA_KEY);
        if (stored) {
          window.sessionStorage.setItem(SUPA_KEY, stored);
          window.localStorage.removeItem(SUPA_KEY);
        }
      }

      // Navigation — isLoading stays true until the new page renders
      if (profile.disabled) {
        router.push('/locked-screen');
        return;
      }
      if (profile.must_change_password) {
        router.push('/onboarding-screen');
        return;
      }
      if (profile.galaxy_name === 'UNNAMED GALAXY') {
        router.push('/onboarding-screen?step=2');
        return;
      }

      router.push('/dashboard');
    } catch {
      setInlineError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        fontFamily: BODY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <style>{CLEAN_CSS}</style>

      <div
        className="cl-in"
        style={{
          width: '100%',
          maxWidth: 380,
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
        }}
      >
        {/* Wordmark */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none" aria-label="AD GALAXY logo" style={{ flexShrink: 0 }}>
              <circle cx="20" cy="20" r="18" stroke={T.line} strokeWidth="1" fill="none" />
              <circle cx="20" cy="20" r="13" stroke={T.faint} strokeWidth="1.4" fill="none" />
              <circle cx="20" cy="20" r="6" fill={ORANGE} />
              <g style={{ transformOrigin: '20px 20px', animation: 'spinCW 7s linear infinite' }}>
                <circle cx="38" cy="20" r="2.6" fill="#3D7BFF" />
              </g>
              <g style={{ transformOrigin: '20px 20px', animation: 'spinCCW 11s linear infinite' }}>
                <circle cx="20" cy="7" r="2" fill="#B98900" />
              </g>
            </svg>
            <div style={{ lineHeight: 1 }}>
              <div style={{ fontFamily: BODY, fontWeight: 700, fontSize: 18, color: T.ink, letterSpacing: 0.5 }}>
                AD <span style={{ color: ORANGE }}>GALAXY</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card */}
        <div
          style={{
            ...cardStyle,
            padding: '28px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/*
           * SECURITY: method is POST (no GET serialisation), action is omitted
           * (no native submission target), noValidate prevents browser-native
           * submission, and handleSubmit calls e.preventDefault() as its very
           * first statement so the native GET/POST never fires.
           */}
          <form
            onSubmit={handleSubmit}
            method="post"
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            {/* USERNAME */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label
                htmlFor="login-username"
                style={{ ...labelStyle, marginBottom: 6, display: 'block' }}
              >
                USERNAME
              </label>
              <input
                id="login-username"
                type="text"
                autoComplete="username"
                placeholder="neo"
                className="input-field"
                aria-invalid={!!usernameError}
                style={{ background: T.soft, border: `1px solid ${T.line}`, color: T.ink }}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
              />
              {usernameError && (
                <p style={{ fontFamily: MONO, fontSize: 9, color: T.red, marginTop: 4 }} role="alert">
                  {usernameError}
                </p>
              )}
            </div>

            {/* PASSWORD */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label
                htmlFor="login-password"
                style={{ ...labelStyle, marginBottom: 6, display: 'block' }}
              >
                PASSWORD
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                placeholder="temporary password from your admin"
                className="input-field"
                aria-invalid={!!passwordError}
                style={{ background: T.soft, border: `1px solid ${T.line}`, color: T.ink }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              {passwordError && (
                <p style={{ fontFamily: MONO, fontSize: 9, color: T.red, marginTop: 4 }} role="alert">
                  {passwordError}
                </p>
              )}
            </div>

            {/* REMEMBER ME */}
            <label
              htmlFor="login-remember"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                id="login-remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                style={{
                  width: 14,
                  height: 14,
                  accentColor: ORANGE,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                }}
              />
              <span style={{
                fontFamily: MONO,
                fontSize: 9,
                color: T.sub,
                letterSpacing: '0.5px',
              }}>
                REMEMBER ME
              </span>
              <span style={{
                fontFamily: MONO,
                fontSize: 8,
                color: T.faint,
                letterSpacing: '0.3px',
              }}>
                · stay signed in across restarts
              </span>
            </label>

            {/* Inline auth error — shown instead of (or alongside) field errors */}
            {inlineError && (
              <div
                role="alert"
                style={{
                  background: `${T.red}18`,
                  border: `1px solid ${T.red}44`,
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontFamily: MONO,
                  fontSize: 10,
                  color: T.red,
                  letterSpacing: '0.4px',
                }}
              >
                {inlineError}
              </div>
            )}

            {/* SIGN IN button — disabled and shows spinner while loading */}
            <button
              type="submit"
              disabled={isLoading}
              className="cl-btn"
              style={{
                ...btnPrimary,
                width: '100%',
                justifyContent: 'center',
                marginTop: 4,
                padding: '11px 15px',
                fontSize: 13,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" opacity="0.25" />
                    <path d="M7 1.5a5.5 5.5 0 015.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Signing in…
                </>
              ) : (
                'SIGN IN'
              )}
            </button>
          </form>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              paddingTop: 4,
              borderTop: `1px solid ${T.line}`,
            }}
          >
            <LockIcon />
            <span
              style={{
                fontFamily: MONO,
                fontSize: 7,
                letterSpacing: '1.6px',
                color: T.faint,
                textAlign: 'center',
              }}
            >
              INVITE ONLY &middot; ACCOUNTS ISSUED BY YOUR ADMIN
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}